import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { BoxIcons, IconTypes } from "us.icons";
import { getRouteUrl } from "us.helper";
import { $Button, $Collapse, $Tooltip } from "us.common/components";
import {
  IMenuProps,
  IOperation,
} from "us.common/components/Menu/MegaMenu/interface";
import { isQuickLink } from "../../../Functions";
import { useSelector } from "react-redux";
import { RootState } from "us.helper/types";

const Menu: React.FC<IMenuProps> = ({
  module,
  quickLinks,
  onAddToQuickLink,
  onRemoveFromQuickLink,
  onNavigate,
}) => {
  const { t } = useTranslation(["US.COLLECTION.COMMON"], {
    useSuspense: true,
  });
  const [selectedMenuLink, setSelectedMenuLink] = useState<IOperation | null>();
  const { name, displayName, features } = module;

  const { isSaving } = useSelector(
    (state: RootState) => state.UserInformation.quickLinks
  );

  /**
   * Converts a display name string into a translation key and retrieves the corresponding translation.
   *
   * The function trims the input string, replaces spaces with underscores, and converts it to uppercase
   * to form a translation key. It then uses the `t` function to fetch the translation from the
   * `US.COMMON:MENU` namespace. If the conversion fails or the key is invalid, it returns the original display name.
   *
   * @param displayName - The display name to convert into a translation key.
   * @returns The translated string if the key is valid and found; otherwise, returns the original display name.
   */
  const convertToTranslationKey = (displayName: string) => {
    try {
      const key = displayName?.trim().split(" ").join("_").toUpperCase();
      if (key) {
        return t(`US.COMMON:MENU.${key}`);
      }
      return displayName;
    } catch (error) {
      console.error(
        `Error converting ${displayName} to translation key:`,
        error
      );
      return displayName;
    }
  };

  return (
    <div>
      <h2 className="font-weight-light ml-4">
        {convertToTranslationKey(displayName)}
      </h2>
      <div className="mt-3 feature-menu-wrapper">
        <$Collapse
          ghost
          defaultActiveKey={Array.from(Array(100).keys())}
          collapsible={"header"}
          expandIconPosition="end"                        
        >
          {features &&
            features.map(
              (feature: {
                id: number;
                displayName: string;
                operations: Array<IOperation>;
              }) => (
                <$Collapse.Panel
                  key={feature.id}
                  header={
                    <span className="feature-group">
                      {convertToTranslationKey(feature.displayName)}
                    </span>
                  }
                >
                  <div className="d-flex flex-column ml-2">
                    {feature &&
                      feature.operations &&
                      feature.operations.map((operation: IOperation) => {
                        const { id, displayName, route, self } = operation;
                        const isAnQuickLink = isQuickLink(
                          quickLinks,
                          operation
                        );
                        return (
                          <div
                            className="feature-link-item d-flex align-items-center justify-content-between"
                            onMouseEnter={() => {
                              if (selectedMenuLink?.id !== id) {
                                setSelectedMenuLink(operation);
                              }
                            }}
                            onMouseLeave={() => setSelectedMenuLink(null)}
                            key={id}
                          >
                            <a
                              className="flex-fill"
                              onClick={() => {
                                self
                                  ? onNavigate(route)
                                  : getRouteUrl.moduleRoute(name, route);
                              }}
                              key={id}
                            >
                              {convertToTranslationKey(displayName)}
                            </a>
                            <span>
                              <$Tooltip
                                placement="left"
                                title={
                                  isAnQuickLink
                                    ? t(
                                        "US.COLLECTION.COMMON:COMMON.UNPIN_FROM_QUICK_LINKS"
                                      )
                                    : t(
                                        "US.COLLECTION.COMMON:COMMON.PIN_TO_QUICK_LINKS"
                                      )
                                }
                              >
                                <$Button
                                  onClick={() =>
                                    isAnQuickLink
                                      ? onRemoveFromQuickLink(operation)
                                      : onAddToQuickLink(operation)
                                  }
                                  size="small"
                                  type="link"
                                  disabled={isSaving}
                                >
                                  {!(
                                    selectedMenuLink &&
                                    selectedMenuLink.id === id
                                  ) &&
                                    isAnQuickLink && (
                                      <BoxIcons
                                        type={IconTypes.BOX_ICON}
                                        name="pin"
                                      />
                                    )}
                                  {selectedMenuLink &&
                                    selectedMenuLink.id === id &&
                                    isAnQuickLink && (
                                      <BoxIcons
                                        type={IconTypes.BOX_ICON}
                                        name="unpin"
                                      />
                                    )}
                                  {selectedMenuLink &&
                                    selectedMenuLink.id === id &&
                                    !isAnQuickLink && (
                                      <BoxIcons
                                        type={IconTypes.BOX_ICON}
                                        name="pin"
                                      />
                                    )}
                                </$Button>
                              </$Tooltip>
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </$Collapse.Panel>
              )
            )}
        </$Collapse>
      </div>
    </div>
  );
};

export default Menu;
